/**
 * `/evaluations/profiles/new` and `/evaluations/profiles/:id` — the
 * full profile editor. Used for both create and edit; the URL slot
 * decides which mode.
 *
 * The file picker sources from the shared test-file pool
 * (`listSharedTestFiles`) and can upload new files inline via the
 * existing `test-files/presign` flow. Org-scoped profiles land in
 * that org's shared pool; global profiles land in the cross-org
 * `_global_/global/` pool visible to every org. All BCBAs at any
 * org can read and write that pool — appropriate for internal-only
 * test fixtures.
 */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type { ChangeEvent, FC } from "react";
import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";

import { Heading, Label } from "@fronterahealth/frontera-ui-components";

import type { MockLearnerData, TestFile } from "@api/rest/prompt-sandbox";
import { useFileKindOptions } from "@api/rest/prompt-sandbox";
import {
  getFileKindLabel,
  getTestFileParseStatus,
  listSharedTestFiles,
  presignTestFile,
  startTestFileParse,
} from "@api/rest/prompt-sandbox";
import {
  REPORT_TYPE_CHOICES,
  createLearnerTestProfile,
  fetchLearnerTestProfile,
  testFileToProfileRef,
  updateLearnerTestProfile,
} from "@api/rest/prompt-test-bed";
import type { LearnerTestProfileFile, ReportType } from "@api/rest/prompt-test-bed";
import { getParsedTestFile, putParsedTestFile, useBasecampOrganizationsRetrieve } from "@api/rest/types-and-hooks";
import { Breadcrumb } from "@components/Breadcrumb/Breadcrumb";
import { MockDataEditor } from "@components/Prompts/MockDataEditor";
import { OrgOwnerSelect } from "@components/Prompts/OrgOwnerSelect";
import { ParseStatusIcon } from "@components/Prompts/ParseStatusIcon";
import { GLOBAL_ORG_SENTINEL } from "@components/Prompts/constants";
import { notifyError, notifySuccess } from "@components/notifications/notifications";
import { TemplateSaveFooter } from "@pages/Templates/TemplateSaveFooter";

const DEFAULT_MOCK_DATA: MockLearnerData = {
  biographic_info: { name: "", age: "", gender: "" },
  date_of_report: new Date().toLocaleDateString("en-US"),
  safety_concerns: [],
  short_term_goals: [],
  provider_language: null,
};

interface FilePickerProps {
  orgId: number | null;
  selectedUuids: Set<string>;
  onToggle: (uuid: string) => void;
}

/** Modal that fetches the current parsed.json for a test file and
 *  writes the edited value back to S3. Unlike the prompt test-bed's
 *  ParsedJsonModal (which overrides for a single run only), edits
 *  here PERSIST — subsequent test runs from any profile pick up the
 *  edited version. */
function EditParsedJsonModal({
  file,
  onClose,
  onSaved,
  onReparse,
}: {
  file: { s3_prefix: string; filename?: string; original_filename?: string };
  onClose: () => void;
  onSaved: () => void;
  /** Optional — when set, adds a "Reparse" button to the footer that
   *  fires the caller's reparse handler and closes the modal. Escape
   *  hatch for "the parse succeeded but the output looked empty." */
  onReparse?: () => void;
}) {
  const displayName = file.original_filename ?? file.filename ?? file.s3_prefix;
  const { data, isError, error } = useQuery({
    queryKey: ["parsed-test-file", file.s3_prefix],
    queryFn: async () => {
      const resp = await getParsedTestFile({ s3_prefix: file.s3_prefix });
      if (resp.status !== 200) {
        const detail = (resp.data as { detail?: string })?.detail ?? `HTTP ${resp.status}`;
        throw new Error(detail);
      }
      return resp.data;
    },
  });
  const [buffer, setBuffer] = useState<string | null>(null);
  useEffect(() => {
    if (buffer !== null) return;
    if (data) {
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setBuffer(data.parsed_json ?? "");
    }
  }, [buffer, data]);

  const saveMutation = useMutation({
    mutationFn: async (parsed_json: string) => {
      const resp = await putParsedTestFile({ s3_prefix: file.s3_prefix, parsed_json });
      if (resp.status !== 200) {
        const detail = (resp.data as { detail?: string })?.detail ?? `HTTP ${resp.status}`;
        throw new Error(detail);
      }
      return resp.data;
    },
    onSuccess: () => {
      notifySuccess("Parsed content saved.");
      onSaved();
      onClose();
    },
    onError: (err) => {
      notifyError(err instanceof Error ? err.message : "Failed to save parsed content.");
    },
  });

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40"
      onClick={onClose}
      onKeyDown={(e) => {
        if (e.key === "Escape") onClose();
      }}
      role="dialog"
      aria-modal="true"
      tabIndex={-1}
    >
      <div
        className="bg-bg-surface rounded-xl shadow-xl w-[min(900px,90vw)] max-h-[85vh] flex flex-col p-6 gap-3"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-baseline gap-3">
          <Heading size="small">Edit parsed content</Heading>
          <code className="text-xs text-text-tertiary truncate min-w-0 flex-1">{displayName}</code>
        </div>
        <p className="text-xs text-text-tertiary">
          Saves overwrite the cached <code>parsed.json</code> in S3 for this file. Every subsequent test run that
          references this file — from any profile — will use the edited version.
        </p>
        {isError ? (
          <div className="p-3 rounded border border-red-200 bg-red-50 text-sm text-red-800">
            Couldn&apos;t load the current parsed content: {error instanceof Error ? error.message : String(error)}
          </div>
        ) : buffer === null ? (
          <div className="p-3 text-sm text-text-tertiary italic">Loading…</div>
        ) : (
          <textarea
            value={buffer}
            onChange={(e) => setBuffer(e.target.value)}
            spellCheck={false}
            className="w-full flex-1 min-h-[40vh] font-mono text-xs p-3 rounded-md border border-border-medium bg-bg-default"
          />
        )}
        {buffer !== null && buffer.trim() === "" && onReparse && (
          // Empty parsed content is the exact symptom that motivates a
          // reparse — nudge the user toward the Reparse button below
          // instead of making them close/hunt for it.
          <div className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded px-2 py-1.5">
            The parsed content is empty. Try Reparse below if the file itself has content.
          </div>
        )}
        <div className="flex items-center gap-3 justify-end pt-2">
          {onReparse && (
            <button
              type="button"
              onClick={() => {
                onReparse();
                onClose();
              }}
              className="text-sm px-3 py-1.5 rounded-md border border-border-medium hover:bg-bg-default mr-auto"
              title="Re-run the parser (useful when the output looked empty or wrong)"
            >
              Reparse
            </button>
          )}
          <button
            type="button"
            onClick={onClose}
            className="text-sm px-3 py-1.5 rounded-md border border-border-medium hover:bg-bg-default"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={() => {
              if (buffer !== null) saveMutation.mutate(buffer);
            }}
            disabled={buffer === null || saveMutation.isPending}
            className="text-sm px-3 py-1.5 rounded-md bg-rio-600 text-white hover:bg-rio-700 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {saveMutation.isPending ? "Saving…" : "Save"}
          </button>
        </div>
      </div>
    </div>
  );
}

function FilePicker({ orgId, selectedUuids, onToggle }: FilePickerProps) {
  const queryClient = useQueryClient();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const { options: fileKindOptions, isLoading: fileKindsLoading } = useFileKindOptions();
  const [uploadKind, setUploadKind] = useState<string>("CLINICAL_NOTES");
  const [editingFile, setEditingFile] = useState<TestFile | null>(null);

  // Null orgId lists the truly-global pool (`_global_/global/`), which
  // is visible to every org and is the storage for org-less profiles.
  const orgIdParam = orgId == null ? null : String(orgId);
  const { data: files = [], isLoading } = useQuery({
    queryKey: ["shared-test-files", orgIdParam],
    queryFn: () => listSharedTestFiles(orgIdParam),
  });
  // The endpoint returns every file in the org's shared pool (or the
  // global pool for orgless profiles) — filter to just the ones this
  // profile has attached. New uploads auto-attach via onToggle in
  // uploadMutation.onSuccess, so this doesn't hide the add path.
  const attachedFiles = files.filter((f) => selectedUuids.has(f.file_uuid));

  // Reparse: kick off a fresh EphemeralFileParsingWorkflow for a
  // file that already exists in the shared pool. Same start-parse
  // endpoint the initial upload used — it stamps meta.json back to
  // ``parse_status: parsing`` and returns a new workflow_id, so the
  // existing poll loop picks it up automatically.
  //
  // Use case: user uploaded a file, parser returned empty JSON (bad
  // OCR / wrong file-kind guess / transient LLM failure). Reparse
  // gives them a retry from the UI instead of "delete + re-upload".
  const reparseMutation = useMutation({
    mutationFn: async (file: {
      file_uuid: string;
      s3_key: string;
      file_kind: string;
      original_filename: string;
      mime_type?: string | null;
    }) => {
      await startTestFileParse({
        shared: true,
        org_id: orgIdParam,
        file_uuid: file.file_uuid,
        s3_key: file.s3_key,
        file_kind: file.file_kind,
        original_filename: file.original_filename,
        mime_type: file.mime_type || "application/octet-stream",
      });
      return file.file_uuid;
    },
    onSuccess: () => {
      // Invalidate the list so parse_status flips back to "parsing" —
      // the poller then handles the rest.
      queryClient.invalidateQueries({ queryKey: ["shared-test-files", orgIdParam] });
    },
  });

  const uploadMutation = useMutation({
    // The template sandbox's TestFilesPanel does this same
    // presign → PUT to S3 → start-parse dance; extracting it into a
    // shared hook is future work. For now the profile editor
    // reimplements the minimum: pick a kind, upload the bytes, kick
    // off the parse workflow, and refresh the list. Poll-for-complete
    // stays out of scope — the parse_status column updates on the
    // next fetch and the user can select the file once it's ready.
    mutationFn: async ({ file, file_kind }: { file: File; file_kind: string }) => {
      const presigned = await presignTestFile({
        shared: true,
        org_id: orgIdParam,
        file_kind,
        mime_type: file.type || "application/octet-stream",
        original_filename: file.name,
      });
      const putResp = await fetch(presigned.upload_url, {
        method: "PUT",
        headers: { "Content-Type": file.type || "application/octet-stream" },
        body: file,
      });
      if (!putResp.ok) throw new Error(`Upload failed (${putResp.status})`);
      await startTestFileParse({
        shared: true,
        org_id: orgIdParam,
        file_uuid: presigned.file_uuid,
        s3_key: presigned.s3_key,
        file_kind,
        original_filename: file.name,
        mime_type: file.type || "application/octet-stream",
      });
      return presigned.file_uuid;
    },
    onSuccess: (uuid) => {
      queryClient.invalidateQueries({ queryKey: ["shared-test-files", orgIdParam] });
      onToggle(uuid);
    },
  });

  const handleFileChosen = (e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    uploadMutation.mutate({ file, file_kind: uploadKind });
    // Reset so uploading the same file twice retriggers.
    e.target.value = "";
  };

  // Poll parse-status for any file still in `parsing` state so meta.json
  // gets flipped to `complete` in S3 (the parse workflow itself doesn't
  // write meta.json — that side-effect lives in the parse-status endpoint,
  // which is only triggered by an external poller). Without this, files
  // uploaded from the profile editor stay stuck on "parsing" forever.
  //
  // Pollers are tracked by file_uuid so we can prune ones that are no
  // longer in the current file list (e.g. after an org switch — the
  // previous pool's parsing uuids should stop polling, not keep hitting
  // the wrong `shared-test-files` cache).
  const pollingRef = useRef<Map<string, ReturnType<typeof setInterval>>>(new Map());
  useEffect(() => {
    const active = pollingRef.current;
    const currentUuids = new Set(files.map((f) => f.file_uuid));
    // Stop pollers for files that dropped out of the current list.
    for (const [uuid, iv] of active) {
      if (!currentUuids.has(uuid)) {
        clearInterval(iv);
        active.delete(uuid);
      }
    }
    // Start pollers for freshly-parsing files.
    for (const f of files) {
      if (f.parse_status !== "parsing" || !f.workflow_id) continue;
      if (active.has(f.file_uuid)) continue;
      const iv = setInterval(async () => {
        try {
          const res = await getTestFileParseStatus(f.workflow_id!, {
            shared: true,
            org_id: orgIdParam,
            file_uuid: f.file_uuid,
          });
          if (res.status === "complete" || res.status === "failed") {
            clearInterval(iv);
            active.delete(f.file_uuid);
            queryClient.invalidateQueries({ queryKey: ["shared-test-files", orgIdParam] });
          }
        } catch {
          /* transient errors — the next tick will retry */
        }
      }, 3000);
      active.set(f.file_uuid, iv);
    }
  }, [files, orgIdParam, queryClient]);

  useEffect(() => {
    // On unmount, stop every active poller.
    const active = pollingRef.current;
    return () => {
      for (const iv of active.values()) clearInterval(iv);
      active.clear();
    };
  }, []);

  return (
    // Column flex so the file-list can absorb remaining height inside
    // the parent card and scroll internally instead of pushing the
    // card / page taller as more files pile up.
    <div className="flex flex-col gap-2 h-full min-h-0">
      <div className="flex items-stretch gap-2 p-2 border border-border-subtle rounded-md bg-bg-default shrink-0">
        <select
          value={uploadKind}
          onChange={(e) => setUploadKind(e.target.value)}
          disabled={fileKindsLoading}
          className="flex-1 min-w-0 text-sm border border-border-subtle rounded px-2 py-1.5 bg-bg-surface"
        >
          {fileKindOptions.map((k) => (
            <option key={k.value} value={k.value}>
              {k.label}
            </option>
          ))}
        </select>
        <input
          ref={fileInputRef}
          type="file"
          onChange={handleFileChosen}
          className="hidden"
          disabled={uploadMutation.isPending}
        />
        <button
          type="button"
          onClick={() => fileInputRef.current?.click()}
          disabled={uploadMutation.isPending || fileKindOptions.length === 0}
          className="flex-1 min-w-0 text-sm font-medium px-3 py-1.5 rounded-md bg-rio-600 text-white hover:bg-rio-700 active:bg-rio-800 transition-colors cursor-pointer disabled:cursor-not-allowed disabled:opacity-50"
        >
          {uploadMutation.isPending ? "Uploading…" : "Upload file"}
        </button>
      </div>
      {uploadMutation.isError && <span className="text-xs text-red-600">Upload failed — try again.</span>}

      {isLoading ? (
        <div className="text-xs text-text-tertiary p-3">Loading files…</div>
      ) : attachedFiles.length === 0 ? (
        <div className="text-xs text-text-tertiary italic p-3 border border-border-subtle rounded-md bg-bg-default">
          No files attached to this profile yet — upload one above.
        </div>
      ) : (
        <div className="border border-border-subtle rounded-md overflow-hidden flex-1 min-h-0 flex flex-col">
          <ul className="divide-y divide-border-subtle overflow-y-auto flex-1 min-h-0">
            {attachedFiles.map((f) => (
              <li key={f.file_uuid} className="flex items-center gap-3 px-3 py-2 hover:bg-bg-default">
                <input
                  type="checkbox"
                  checked={selectedUuids.has(f.file_uuid)}
                  onChange={() => onToggle(f.file_uuid)}
                />
                <span className="text-sm text-text-primary flex-1 min-w-0 truncate">{f.original_filename}</span>
                <span className="text-xs text-text-tertiary shrink-0">{getFileKindLabel(f.file_kind)}</span>
                <ParseStatusIcon status={f.parse_status} />
                {f.parse_status === "complete" && (
                  // "Edit parsed" is the only row-level action for
                  // healthy files. Reparse is available from the modal
                  // footer for the "content looked empty" escape hatch
                  // — putting it inline on every row made healthy rows
                  // read as cluttered.
                  <button
                    type="button"
                    onClick={() => setEditingFile(f)}
                    className="text-xs text-rio-700 hover:text-rio-800 underline shrink-0"
                    title="View / edit the cached parsed.json for this file"
                  >
                    Edit parsed
                  </button>
                )}
                {f.parse_status === "failed" && (
                  // Failure is the natural place to show Reparse
                  // prominently — the parser blew up and the user's
                  // next action is almost certainly "try again."
                  // ``text-red-600`` (not the ``text-text-error-primary``
                  // token) because the token variant read as muted /
                  // similar to the healthy row's rio-700 link color;
                  // ``red-600`` matches the delete-button treatment
                  // elsewhere in Evaluations and is unambiguously red.
                  <button
                    type="button"
                    onClick={() => reparseMutation.mutate(f)}
                    disabled={reparseMutation.isPending}
                    className="text-xs text-red-600 hover:text-red-700 underline shrink-0 disabled:opacity-50 font-medium"
                    title="The parser failed — try again"
                  >
                    Reparse
                  </button>
                )}
                {f.parse_status === "pending" && (
                  // Pre-parse state: parse hasn't fired yet (or was
                  // aborted). Slightly less prominent than the failed
                  // case but still visible.
                  <button
                    type="button"
                    onClick={() => reparseMutation.mutate(f)}
                    disabled={reparseMutation.isPending}
                    className="text-xs text-amber-700 hover:underline shrink-0 disabled:opacity-50"
                    title="Parse hasn't run yet — start it"
                  >
                    Parse
                  </button>
                )}
              </li>
            ))}
          </ul>
        </div>
      )}
      {editingFile &&
        (() => {
          // Derive the S3 prefix from the file's ``s3_key`` — the
          // parsed.json cache lives at ``<prefix>/parsed.json``, one
          // directory above the raw upload.
          const s3_prefix = editingFile.s3_key.substring(0, editingFile.s3_key.lastIndexOf("/"));
          return (
            <EditParsedJsonModal
              file={{ s3_prefix, original_filename: editingFile.original_filename }}
              onClose={() => setEditingFile(null)}
              onSaved={() => {
                // Invalidate the parsed-content cache so any concurrent
                // consumer (e.g. the prompt test-bed reading via the
                // same query key) re-fetches. The file list itself
                // doesn't need refresh — parse_status was already
                // "complete" and stays that way.
                queryClient.invalidateQueries({ queryKey: ["parsed-test-file", s3_prefix] });
              }}
              onReparse={() => reparseMutation.mutate(editingFile)}
            />
          );
        })()}
    </div>
  );
}

export const LearnerTestProfileEditor: FC = () => {
  const { id } = useParams<{ id: string }>();
  const isEdit = id !== undefined && id !== "new";
  const profileId = isEdit ? Number(id) : null;
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  // Form state — seeded from the fetched profile when editing.
  const [name, setName] = useState("");
  const [orgId, setOrgId] = useState<number | null>(null);
  const [reportTypes, setReportTypes] = useState<ReportType[]>([]);
  const [notes, setNotes] = useState("");
  const [mockData, setMockData] = useState<MockLearnerData>(DEFAULT_MOCK_DATA);
  const [selectedFileUuids, setSelectedFileUuids] = useState<Set<string>>(new Set());
  // In edit mode we can't render MockDataEditor before the seed runs —
  // it initializes local state from `value.custom_fields` exactly once,
  // and if it mounts with the DEFAULT (empty) mock data, later saves
  // clobber the DB's custom_fields with the empty set. Create mode
  // has nothing to seed so it's ready immediately.
  const [seeded, setSeeded] = useState<boolean>(!isEdit);

  const { data: existing } = useQuery({
    queryKey: ["learner-test-profile", profileId],
    queryFn: () => fetchLearnerTestProfile(profileId!),
    enabled: profileId != null,
  });

  const { data: orgsResponse } = useBasecampOrganizationsRetrieve({});
  const orgs = useMemo(
    () =>
      orgsResponse?.status === 200 ? (Array.isArray(orgsResponse.data) ? orgsResponse.data : [orgsResponse.data]) : [],
    [orgsResponse],
  );

  // Seed the form from the fetched profile exactly once per URL id.
  // A per-instance ref (rather than a module-level flag) resets on
  // navigation between `/profiles/:a` and `/profiles/:b` when React
  // Router remounts the component; if it doesn't remount (same URL
  // component staying mounted while `id` changes), the id-tracked ref
  // still forces a re-seed. Refetches of the SAME id leave the form
  // alone so a background refresh doesn't clobber in-progress edits.
  const seededForId = useRef<number | null>(null);
  useEffect(() => {
    if (!existing) return;
    if (seededForId.current === existing.id) return;
    seededForId.current = existing.id;
    setName(existing.name);
    setOrgId(existing.organization_id);
    setReportTypes(existing.report_types);
    setNotes(existing.notes ?? "");
    setMockData(existing.mock_learner_data ?? DEFAULT_MOCK_DATA);
    setSelectedFileUuids(new Set(existing.test_files.map((f) => f.file_uuid)));
    setSeeded(true);
  }, [existing]);

  // Mirror the FilePicker's cache key so it and the outer editor share
  // one shared-files fetch per org (null = truly-global pool).
  const outerOrgIdParam = orgId == null ? null : String(orgId);
  const { data: sharedFiles = [] } = useQuery({
    queryKey: ["shared-test-files", outerOrgIdParam],
    queryFn: () => listSharedTestFiles(outerOrgIdParam),
  });

  // Full TestFile objects keyed by uuid — the source used to build the
  // `test_files` array on save (uuid + s3_prefix + kind + filename).
  //
  // The `existing` backfill preserves refs to files that aren't (or are
  // no longer) in the shared pool — e.g. a file uploaded then deleted
  // from that org. Live shared-pool entries win the merge so re-parsed
  // metadata takes precedence.
  const orgFileIndex = useMemo<Record<string, TestFile>>(() => {
    const next: Record<string, TestFile> = {};
    for (const ref of existing?.test_files ?? []) {
      next[ref.file_uuid] = {
        file_uuid: ref.file_uuid,
        original_filename: ref.filename,
        file_kind: ref.file_kind,
        mime_type: "",
        s3_key: `${ref.s3_prefix}/raw`,
        uploaded_at: "",
        parse_status: "complete",
      };
    }
    for (const f of sharedFiles) next[f.file_uuid] = f;
    return next;
  }, [existing, sharedFiles]);

  // When the user switches org, drop selections that don't exist in the
  // new pool. Otherwise Save would persist cross-org file refs (or
  // silently drop them, which the user can't see). We only prune AFTER
  // the initial seed so we don't clobber the just-loaded selection
  // while `sharedFiles` is still fetching.
  const priorOrgIdParam = useRef<string | null | undefined>(undefined);
  useEffect(() => {
    if (!seeded) return;
    if (priorOrgIdParam.current === undefined) {
      priorOrgIdParam.current = outerOrgIdParam;
      return;
    }
    if (priorOrgIdParam.current === outerOrgIdParam) return;
    priorOrgIdParam.current = outerOrgIdParam;
    setSelectedFileUuids((prev) => {
      const kept = new Set<string>();
      for (const uuid of prev) if (orgFileIndex[uuid]) kept.add(uuid);
      return kept;
    });
  }, [outerOrgIdParam, seeded, orgFileIndex]);

  const saveMutation = useMutation({
    mutationFn: async () => {
      const test_files: LearnerTestProfileFile[] = Array.from(selectedFileUuids)
        .map((uuid) => orgFileIndex[uuid])
        .filter((f): f is TestFile => !!f)
        .map(testFileToProfileRef);
      const input = {
        name: name.trim(),
        organization_id: orgId,
        report_types: reportTypes,
        notes: notes.trim(),
        mock_learner_data: mockData,
        test_files,
      };
      if (isEdit && profileId != null) {
        return updateLearnerTestProfile(profileId, input);
      }
      return createLearnerTestProfile(input);
    },
    onSuccess: (profile) => {
      queryClient.invalidateQueries({ queryKey: ["learner-test-profiles"] });
      if (isEdit) {
        // Push the fresh server payload into the individual profile
        // cache so the footer's `isDirty` comparison (which diffs
        // against `existing`) flips to clean immediately — otherwise
        // the footer would keep showing "Unsaved changes" until the
        // next background refetch.
        queryClient.setQueryData(["learner-test-profile", profile.id], profile);
        notifySuccess("Profile saved");
      } else {
        // Fresh create — route back to the list so the user sees the
        // new row (their confirmation) instead of landing silently on
        // the edit URL with a button that swapped to "Save changes".
        notifySuccess(`Created "${profile.name}"`);
        navigate("/evaluations/profiles");
      }
    },
    onError: () => notifyError("Couldn't save the profile — try again."),
  });

  const toggleReportType = (v: ReportType) => {
    setReportTypes((prev) => (prev.includes(v) ? prev.filter((x) => x !== v) : [...prev, v]));
  };

  const canSave = name.trim().length > 0 && !saveMutation.isPending;

  // Compare the current in-memory form against the last-fetched profile
  // so the footer can show "Unsaved changes" / "Last saved …". JSON
  // comparison is fine — payloads are small and this only recomputes
  // when a form field mutates.
  const isDirty = useMemo(() => {
    if (!isEdit) return true;
    if (!existing) return false;
    const currentTestFileIds = Array.from(selectedFileUuids).sort();
    const savedTestFileIds = existing.test_files.map((f) => f.file_uuid).sort();
    return (
      name !== existing.name ||
      orgId !== existing.organization_id ||
      notes !== (existing.notes ?? "") ||
      JSON.stringify(reportTypes) !== JSON.stringify(existing.report_types) ||
      JSON.stringify(mockData) !== JSON.stringify(existing.mock_learner_data ?? DEFAULT_MOCK_DATA) ||
      JSON.stringify(currentTestFileIds) !== JSON.stringify(savedTestFileIds)
    );
  }, [isEdit, existing, name, orgId, notes, reportTypes, mockData, selectedFileUuids]);

  const lastSavedAt = existing?.last_updated_date ? new Date(existing.last_updated_date) : undefined;

  const handleDiscard = () => {
    // Cancel-in-place: reset to the last-fetched snapshot without
    // leaving the page. For a fresh create, "discard" navigates back.
    if (!isEdit || !existing) {
      navigate("/evaluations/profiles");
      return;
    }
    setName(existing.name);
    setOrgId(existing.organization_id);
    setReportTypes(existing.report_types);
    setNotes(existing.notes ?? "");
    setMockData(existing.mock_learner_data ?? DEFAULT_MOCK_DATA);
    setSelectedFileUuids(new Set(existing.test_files.map((f) => f.file_uuid)));
  };

  return (
    // `h-full min-h-0` on the page + `flex-1 min-h-0` on the grid so the
    // two column cards fill the remaining viewport height and scroll
    // internally instead of pushing the page to grow (the "page has a
    // scrollbar even though only the file list expanded" issue).
    <div className="flex flex-col bg-bg-default h-full min-h-0 px-4 sm:px-8 lg:px-16 py-8">
      <Breadcrumb
        crumbs={[
          { label: "Profiles", to: "/evaluations/profiles" },
          { label: isEdit ? (existing?.name ?? "…") : "New" },
        ]}
      />

      <div className="flex flex-row items-center justify-between gap-3 mb-6 flex-wrap">
        <Heading size="medium">{isEdit ? "Edit profile" : "New profile"}</Heading>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 flex-1 min-h-0">
        {/* Left column: metadata block + test-files block, each in its
            own bordered panel so they read as separate cards. The
            metadata card is shrink-to-content; the test-files card
            fills the rest of the column and scrolls internally so more
            files don't push a page-level scrollbar. */}
        <div className="flex flex-col gap-6 min-h-0">
          <div className="flex flex-col gap-4 p-6 bg-bg-surface border border-border-medium rounded-xl shrink-0">
            <div className="flex flex-col gap-1">
              <Label size="small" colorType="secondary">
                Name
              </Label>
              <input
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="e.g. Diagnostic — verbal 6yo with autism"
                className="text-sm border border-border-subtle rounded px-2 py-1.5 bg-bg-default focus:outline-none focus:ring-1 focus:ring-limestone-900"
              />
            </div>

            <div className="flex flex-col gap-1">
              <OrgOwnerSelect
                orgs={orgs}
                value={orgId == null ? GLOBAL_ORG_SENTINEL : String(orgId)}
                onChange={(v) => setOrgId(v === GLOBAL_ORG_SENTINEL ? null : Number(v))}
              />
              <span className="text-xs text-text-tertiary">
                Global profiles are visible to every org and share a cross-org file pool. Pick a specific org to keep
                the profile and its files scoped to that org only.
              </span>
            </div>

            <div className="flex flex-col gap-1">
              <Label size="small" colorType="secondary">
                Report types
              </Label>
              <div className="flex flex-wrap gap-x-4 gap-y-1 border border-border-subtle rounded px-2 py-1.5 bg-bg-default">
                {REPORT_TYPE_CHOICES.map((choice) => (
                  <label key={choice.value} className="flex items-center gap-2 text-sm">
                    <input
                      type="checkbox"
                      checked={reportTypes.includes(choice.value)}
                      onChange={() => toggleReportType(choice.value)}
                    />
                    <span>{choice.label}</span>
                  </label>
                ))}
              </div>
              <span className="text-xs text-text-tertiary">
                Leave all unchecked to make the profile usable in any report-type context.
              </span>
            </div>

            <div className="flex flex-col gap-1">
              <Label size="small" colorType="secondary">
                Description
              </Label>
              <textarea
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                rows={3}
                placeholder="What's this profile good for? Which prompts is it designed for?"
                className="text-sm border border-border-subtle rounded px-2 py-1.5 bg-bg-default focus:outline-none focus:ring-1 focus:ring-limestone-900"
              />
            </div>
          </div>
          {/* Second card in the left column: test files. Fills remaining
              vertical space; the file picker inside scrolls its own list. */}
          <div className="flex flex-col gap-4 p-6 bg-bg-surface border border-border-medium rounded-xl flex-1 min-h-0">
            <Heading size="small">Test files</Heading>
            <div className="flex-1 min-h-0 overflow-hidden">
              <FilePicker
                orgId={orgId}
                selectedUuids={selectedFileUuids}
                onToggle={(uuid) =>
                  setSelectedFileUuids((prev) => {
                    const next = new Set(prev);
                    if (next.has(uuid)) next.delete(uuid);
                    else next.add(uuid);
                    return next;
                  })
                }
              />
            </div>
          </div>
        </div>

        {/* Right column: mock data fills the full column height and
            scrolls internally so custom-fields overflow doesn't push
            a page-level scrollbar. */}
        <div className="flex flex-col gap-4 p-6 bg-bg-surface border border-border-medium rounded-xl min-h-0">
          <Heading size="small">Test inputs</Heading>
          {seeded ? (
            <div className="flex-1 min-h-0 overflow-y-auto">
              <MockDataEditor value={mockData} onChange={setMockData} />
            </div>
          ) : (
            <div className="text-xs text-text-tertiary p-3 border border-border-subtle rounded-md bg-bg-default">
              Loading profile…
            </div>
          )}
        </div>
      </div>

      {/* Bottom padding so the fixed footer doesn't cover content when
          the columns get short (empty file list, no custom fields). */}
      <div className="h-16 shrink-0" />
      <TemplateSaveFooter
        isDirty={isDirty}
        isSaving={saveMutation.isPending}
        onSave={() => saveMutation.mutate()}
        onDiscard={handleDiscard}
        saveLabel={isEdit ? "Save changes" : "Create profile"}
        discardLabel={isEdit ? "Discard" : "Cancel"}
        saveDisabled={!canSave}
        lastSavedAt={isEdit ? lastSavedAt : undefined}
        alwaysShowDiscard={!isEdit}
      />
    </div>
  );
};

/** Wrapper that keys the editor to the URL `:id` param so navigating
 *  between different profile ids (e.g. `/profiles/2` → `/profiles/3`)
 *  fully remounts the editor and wipes any stale local form state.
 *  App.tsx uses this as the element for the `:id` route. */
export const LearnerTestProfileEditorKeyed: FC = () => {
  const { id } = useParams<{ id: string }>();
  return <LearnerTestProfileEditor key={id ?? "unknown"} />;
};
